/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

/**
 * Button Variants Reference
 *
 * NAMING NOTE: Our variant names follow brand hierarchy, not typical conventions:
 *
 * | Variant     | Visual             | Use Case                              |
 * |-------------|--------------------|---------------------------------------|
 * | default     | Brand filled       | Primary CTAs - main action per screen |
 * | neutral     | Ink filled         | Secondary actions, form submits       |
 * | outline     | Surface + border   | Tertiary actions, cancel buttons      |
 * | secondary   | Accent filled      | Theme-color secondary CTAs            |
 * | ghost       | Transparent        | Navigation, toolbar actions           |
 * | destructive | Danger filled      | Delete, remove actions                |
 * | link        | Text + underline   | Inline links                          |
 */

const BASE_BUTTON_CLASSES =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-button)] border border-transparent text-sm font-medium transition-[color,background-color,border-color,box-shadow,filter,transform] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-frame)] disabled:pointer-events-none disabled:opacity-50 [&>svg]:pointer-events-none [&>svg]:shrink-0 [&>svg]:mr-0 active:translate-y-px active:brightness-95";

const buttonVariants = cva(BASE_BUTTON_CLASSES, {
  variants: {
    variant: {
      // Primary CTA: organization brand - use for main action per screen
      default:
        "bg-brand text-brand-foreground shadow-[0_1px_2px_hsl(220_20%_10%_/_0.12)] hover:brightness-[0.96] active:brightness-[0.92]",
      // Secondary action: app ink - use when brand emphasis would compete
      neutral:
        "bg-[var(--text-primary)] text-[var(--surface-frame)] shadow-[0_1px_2px_hsl(220_20%_10%_/_0.12)] hover:brightness-[0.94] active:brightness-[0.9]",
      // Danger action - use for delete, destructive operations
      destructive:
        "bg-[var(--status-danger)] text-white shadow-[0_1px_2px_hsl(220_20%_10%_/_0.12)] hover:brightness-[0.95] active:brightness-[0.9]",
      // Tertiary: bordered surface - use for cancel, alternative actions
      outline:
        "border-[var(--border-hairline)] bg-surface-frame text-text-primary shadow-[var(--shadow-panel)] hover:bg-surface-wash active:bg-surface-secondary-nav",
      // Accent CTA: theme secondary color - use when a secondary action should carry brand color
      secondary:
        "border-[var(--brand-accent)] bg-brand-accent text-[hsl(var(--secondary-foreground))] shadow-[0_1px_2px_hsl(220_20%_10%_/_0.1)] hover:brightness-[0.97] active:brightness-[0.92]",
      // Minimal: No background - use for navigation, toolbars
      ghost:
        "text-text-secondary hover:bg-brand-soft hover:text-text-primary active:bg-surface-secondary-nav",
      // Text link style
      link: "border-transparent text-brand underline-offset-4 hover:underline hover:brightness-[0.9]",
    },
    size: {
      default: "h-9 px-3.5 py-1.5",
      sm: "h-8 px-3 text-xs",
      lg: "h-10 px-4",
      pill: "h-10 rounded-pill px-4 text-sm",
      "pill-lg": "h-11 rounded-pill px-6 text-sm font-medium",
      icon: "h-9 w-9",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: string;
}

const Button = React.memo(
  React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        className,
        variant,
        size,
        asChild = false,
        isLoading = false,
        loadingText,
        disabled,
        children,
        style: incomingStyle,
        ...props
      },
      ref,
    ) => {
      const Comp = asChild ? Slot : "button";
      const resolvedType =
        props.type ??
        (asChild
          ? undefined
          : ("button" as React.ButtonHTMLAttributes<HTMLButtonElement>["type"]));
      const isDisabled = disabled ?? isLoading;

      // When rendering as a Slot (asChild), we must pass exactly one child element.
      // Avoid injecting additional wrapper elements (e.g., loading spinner) to prevent Slot errors.
      if (asChild) {
        return (
          <Comp
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            aria-busy={isLoading ? "true" : undefined}
            aria-disabled={isDisabled ? "true" : undefined}
            disabled={isDisabled}
            style={incomingStyle}
            {...props}
          >
            {children}
          </Comp>
        );
      }

      // Render children as direct flex children of the outer button so
      // caller-provided layout overrides (`justify-between`, `justify-end`)
      // actually apply. A previous version wrapped children in an inner
      // `inline-flex justify-center gap-2` span which sized to content and
      // centered, silently overriding `justify-between` on dropdown
      // triggers — every caret ended up glued to the label instead of
      // sitting at the right edge of the button.
      //
      // The inner wrapper is only retained around loading content so the
      // `opacity-90` dim cue stays scoped to text/children while the
      // spinner stays bright. A loading button is transient and not the
      // visual default, so the brief layout collapse during loading is an
      // acceptable trade.
      return (
        <Comp
          className={cn(
            buttonVariants({ variant, size, className }),
            isLoading && "relative",
          )}
          ref={ref}
          aria-busy={isLoading ? "true" : undefined}
          aria-disabled={isDisabled ? "true" : undefined}
          disabled={isDisabled}
          style={incomingStyle}
          type={resolvedType}
          {...props}
        >
          {isLoading && (
            <span className="inline-flex items-center" aria-hidden>
              <LoadingSpinner size="sm" />
            </span>
          )}
          {isLoading ? (
            <span className="inline-flex items-center gap-2 opacity-90 [&>svg]:pointer-events-none [&>svg]:shrink-0 [&>svg]:mr-0">
              {loadingText ?? children}
            </span>
          ) : (
            children
          )}
        </Comp>
      );
    },
  ),
);
Button.displayName = "Button";

export { Button, buttonVariants };
